import { api, ApiError, get } from "./api";
import * as store from "./store";
import type { Photo, Plant } from "./types";
import { isDemo } from "./demo";

export type SyncState = { pending: number; online: boolean; syncing: boolean; error: string };
const state: SyncState = { pending: 0, online: navigator.onLine, syncing: false, error: "" };
const listeners = new Set<(s: SyncState) => void>();
const changeListeners = new Set<(gardenId: string) => void>();

export function onSyncState(fn: (s: SyncState) => void) { listeners.add(fn); fn({ ...state }); return () => listeners.delete(fn); }
export function onRemoteChange(fn: (gardenId: string) => void) { changeListeners.add(fn); return () => changeListeners.delete(fn); }
function emit() { for (const f of listeners) f({ ...state }); }

let running: Promise<void> | null = null;
let again = false;
let activeGarden: string | null = null;
export function setActiveGarden(id: string | null) { activeGarden = id; }

export function syncSoon() {
  if (running) { again = true; return running; }
  running = (async () => {
    do {
      again = false;
      await syncOnce();
    } while (again);
  })().finally(() => { running = null; });
  return running;
}

async function syncOnce() {
  if (isDemo()) { state.online = true; state.pending = 0; state.error = ""; emit(); return; }
  state.syncing = true; emit();
  try {
    await push();
    if (activeGarden) await pull(activeGarden);
    state.online = true; state.error = "";
  } catch (e) {
    if (e instanceof ApiError && e.status === 0) state.online = false;
    else state.error = e instanceof Error ? e.message : String(e);
  } finally {
    state.pending = await store.pendingCount();
    state.syncing = false; emit();
  }
}

async function push() {
  for (const op of await store.outbox()) {
    try {
      if (op.type === "plant") {
        const t = await store.getPlant(op.id);
        if (!t) { await store.unqueue(op.key); continue; }
        const { id, deleted_at, ...body } = t;
        const r = await api<{ plant: Plant }>("PUT", "/api/plants/" + id, body);
        const fresh = await store.outbox();
        // keep the local row if it was edited again while this request was in flight
        if (!fresh.find((o) => o.key === op.key && o.at !== op.at)) await store.putPlant(normalizePlant(r.plant));
      } else if (op.type === "plant-delete") {
        await api("DELETE", "/api/plants/" + op.id);
      } else if (op.type === "photo") {
        const p = await store.getPhoto(op.id);
        if (!p || !p.blob) { await store.unqueue(op.key); continue; }
        const fd = new FormData();
        fd.append("id", p.id); fd.append("plant_id", p.plant_id); fd.append("kind", p.kind);
        fd.append("file", p.blob, p.id + ".jpg");
        await api("POST", "/api/photos", fd);
        await store.putPhoto({ ...p, uploaded: true });
      } else if (op.type === "photo-delete") {
        await api("DELETE", "/api/photos/" + op.id);
      }
      const cur = (await store.outbox()).find((o) => o.key === op.key);
      if (cur && cur.at === op.at) await store.unqueue(op.key);
    } catch (e) {
      if (e instanceof ApiError && e.status === 0) throw e; // offline: stop and retry later
      if (e instanceof ApiError && e.status === 401) throw e;
      // photo whose plant is not on the server yet, or a rejected row: try the rest, keep this one
      console.warn("sync op failed", op, e);
    }
  }
}

function normalizePlant(t: any): Plant {
  return { ...t, lat: Number(t.lat), lng: Number(t.lng) } as Plant;
}

async function pull(gardenId: string) {
  const curKey = "cursor:" + gardenId;
  let cursor = await store.getMeta<{ t: number; p: number }>(curKey, { t: 0, p: 0 });
  let changed = false;
  for (let round = 0; round < 20; round++) {
    const r = await get<{ plants: any[]; photos: any[]; cursor: { t: number; p: number } }>(
      "/api/gardens/" + gardenId + "/sync?t=" + cursor.t + "&p=" + cursor.p);
    const pending = new Set((await store.outbox()).map((o) => o.id));
    for (const t of r.plants) {
      if (pending.has(t.id)) continue;
      await store.putPlant(normalizePlant(t)); changed = true;
    }
    for (const p of r.photos) {
      if (pending.has(p.id)) continue;
      const local = await store.getPhoto(p.id);
      const merged: Photo = { ...(local ?? {}), ...p, uploaded: true };
      await store.putPhoto(merged); changed = true;
    }
    const done = r.cursor.t === cursor.t && r.cursor.p === cursor.p;
    cursor = r.cursor;
    await store.setMeta(curKey, cursor);
    if (done || (r.plants.length < 2000 && r.photos.length < 5000)) break;
  }
  if (changed) for (const f of changeListeners) f(gardenId);
}

window.addEventListener("online", () => { state.online = true; emit(); syncSoon(); });
window.addEventListener("offline", () => { state.online = false; emit(); });
setInterval(() => { if (navigator.onLine) syncSoon(); }, 30000);
document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") syncSoon(); });
